import { type DependencyList, useEffect } from "react";

import { useTimeoutFunction } from "@/hooks/use-timeout-function";

export type UseDebounceReturn = [() => boolean | null, () => void];

/**
 * A hook that debounces a function call by delaying its execution until after a specified time has elapsed
 * since the last time it was invoked.
 *
 * @param fn - The function to debounce
 * @param ms - The number of milliseconds to delay, defaults to 0
 * @param deps - Array of dependencies that will reset the debounce timer when changed
 * @returns A tuple containing:
 *          - isReady: Function that returns the current state (true if ready, false if pending, null if cancelled)
 *          - cancel: Function to cancel the pending execution
 */
export function useDebounce(
  fn: () => void,
  ms = 0,
  deps: DependencyList = []
): UseDebounceReturn {
  const [isReady, cancel, reset] = useTimeoutFunction(fn, ms);

  useEffect(reset, deps);

  return [isReady, cancel];
}
